import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';

export interface BatchLayerData {
  batch_layer_id: string;
  product_id: string;
  product_name: string;
  procurement_id: string | null;
  initial_quantity: number;
  remaining_quantity: number;
  consumed_quantity: number;
  unit_cost: number;
  total_remaining_value: number;
  received_date: string;
  age_days: number;
}

export interface BatchConsumptionData {
  id: string;
  batch_layer_id: string;
  consumption_type: string;
  quantity_consumed: number;
  unit_cost_at_consumption: number;
  reference_date: string;
  reference_id: string | null;
  notes: string | null;
  created_at: string;
  product_name?: string;
}

export interface BatchCogsSummary {
  product_id: string;
  product_name: string;
  total_quantity: number;  // Field name from RPC
  total_cogs: number;
  avg_unit_cost: number;
}

export interface BatchTraceabilityReport {
  batchLayers: BatchLayerData[];
  consumptionLog: BatchConsumptionData[];
  cogsSummary: BatchCogsSummary[];
  summary: {
    totalBatches: number;
    totalRemainingQuantity: number;
    totalRemainingValue: number;
    totalConsumed: number;
    totalCogs: number;
    avgBatchAge: number;
  };
}

// Fetch batch layer status for all products or a specific product
export async function getBatchLayerData(productId?: string): Promise<BatchLayerData[]> {
  const { data, error } = await supabase.rpc('get_batch_layer_status', {
    p_product_id: productId || null
  });
  
  if (error) throw error;
  return (data || []) as BatchLayerData[];
}

// Fetch consumption log with product names
export async function getBatchConsumptionLog(filters?: {
  productId?: string;
  consumptionType?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}): Promise<BatchConsumptionData[]> {
  let query = supabase
    .from('batch_consumption_log')
    .select(`
      *,
      batch_layer:inventory_batch_layers(
        product:products(name)
      )
    `)
    .order('created_at', { ascending: false });

  if (filters?.consumptionType) {
    query = query.eq('consumption_type', filters.consumptionType);
  }

  if (filters?.startDate) {
    query = query.gte('reference_date', filters.startDate);
  }

  if (filters?.endDate) {
    query = query.lte('reference_date', filters.endDate);
  }

  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  const { data, error } = await query;
  
  if (error) throw error;
  
  // Transform to include product name at top level
  return (data || []).map((item: any) => ({
    id: item.id,
    batch_layer_id: item.batch_layer_id,
    consumption_type: item.consumption_type,
    quantity_consumed: item.quantity_consumed,
    unit_cost_at_consumption: item.unit_cost_at_consumption,
    reference_date: item.reference_date,
    reference_id: item.reference_id,
    notes: item.notes,
    created_at: item.created_at,
    product_name: item.batch_layer?.product?.name || 'Unknown'
  }));
}

// Get COGS summary by product for a date range
export async function getBatchCogsSummary(
  startDate: string,
  endDate: string
): Promise<BatchCogsSummary[]> {
  const { data, error } = await supabase.rpc('get_batch_cogs_summary', {
    p_start_date: startDate,
    p_end_date: endDate
  });
  
  if (error) throw error;
  return (data || []) as BatchCogsSummary[];
}

// Compile full traceability report
export async function getBatchTraceabilityReport(
  startDate?: string,
  endDate?: string,
  productId?: string
): Promise<BatchTraceabilityReport> {
  const today = format(new Date(), 'yyyy-MM-dd');
  const thirtyDaysAgo = format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
  
  const effectiveStartDate = startDate || thirtyDaysAgo;
  const effectiveEndDate = endDate || today;

  // Fetch all data in parallel
  const [batchLayers, consumptionLog, cogsSummary] = await Promise.all([
    getBatchLayerData(productId),
    getBatchConsumptionLog({
      productId,
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
      limit: 500
    }),
    getBatchCogsSummary(effectiveStartDate, effectiveEndDate)
  ]);

  // Filter batch layers by product if specified
  const filteredLayers = productId 
    ? batchLayers.filter(b => b.product_id === productId)
    : batchLayers;

  // Calculate summary
  const summary = {
    totalBatches: filteredLayers.length,
    totalRemainingQuantity: filteredLayers.reduce((sum, b) => sum + b.remaining_quantity, 0),
    totalRemainingValue: filteredLayers.reduce((sum, b) => sum + b.total_remaining_value, 0),
    totalConsumed: consumptionLog.reduce((sum, c) => sum + c.quantity_consumed, 0),
    totalCogs: cogsSummary.reduce((sum, c) => sum + (c.total_cogs || 0), 0),
    avgBatchAge: filteredLayers.length > 0 
      ? filteredLayers.reduce((sum, b) => sum + b.age_days, 0) / filteredLayers.length 
      : 0
  };

  return {
    batchLayers: filteredLayers,
    consumptionLog,
    cogsSummary,
    summary
  };
}

// Export report to Excel
export function exportBatchTraceabilityToExcel(
  report: BatchTraceabilityReport,
  filename: string,
  dateRange: { start: string; end: string }
) {
  const workbook = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ['Batch Traceability Report'],
    [''],
    ['Report Period', `${dateRange.start} - ${dateRange.end}`],
    [''],
    ['Summary Metrics'],
    ['Total Active Batches', report.summary.totalBatches],
    ['Total Remaining Quantity (kg)', report.summary.totalRemainingQuantity.toFixed(2)],
    ['Total Remaining Value (ETB)', report.summary.totalRemainingValue.toFixed(2)],
    ['Total Consumed in Period (kg)', report.summary.totalConsumed.toFixed(2)],
    ['Total COGS (ETB)', report.summary.totalCogs.toFixed(2)],
    ['Average Batch Age (days)', report.summary.avgBatchAge.toFixed(1)],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Batch Layers sheet
  const batchData = report.batchLayers.map((b) => ({
    'Product': b.product_name,
    'Initial Qty (kg)': b.initial_quantity.toFixed(2),
    'Consumed (kg)': b.consumed_quantity.toFixed(2),
    'Remaining (kg)': b.remaining_quantity.toFixed(2),
    'Unit Cost (ETB)': b.unit_cost.toFixed(2),
    'Remaining Value (ETB)': b.total_remaining_value.toFixed(2),
    'Received Date': format(new Date(b.received_date), 'yyyy-MM-dd'),
    'Age (days)': b.age_days,
    'Procurement ID': b.procurement_id?.slice(0, 8) || '-',
  }));
  const batchSheet = XLSX.utils.json_to_sheet(batchData);
  XLSX.utils.book_append_sheet(workbook, batchSheet, 'Batch Layers');

  // COGS Summary sheet
  const cogsData = report.cogsSummary.map((c) => ({
    'Product': c.product_name,
    'Qty Consumed (kg)': c.total_quantity.toFixed(2),
    'Total COGS (ETB)': c.total_cogs.toFixed(2),
    'Avg Unit Cost (ETB)': c.avg_unit_cost.toFixed(2),
  }));
  const cogsSheet = XLSX.utils.json_to_sheet(cogsData);
  XLSX.utils.book_append_sheet(workbook, cogsSheet, 'COGS by Product');

  // Consumption Log sheet
  const consumptionData = report.consumptionLog.map((c) => ({
    'Date': format(new Date(c.reference_date), 'yyyy-MM-dd'),
    'Product': c.product_name,
    'Type': c.consumption_type,
    'Qty Consumed (kg)': c.quantity_consumed.toFixed(2),
    'Unit Cost (ETB)': c.unit_cost_at_consumption.toFixed(2),
    'Total Cost (ETB)': (c.quantity_consumed * c.unit_cost_at_consumption).toFixed(2),
    'Notes': c.notes || '-',
  }));
  const consumptionSheet = XLSX.utils.json_to_sheet(consumptionData);
  XLSX.utils.book_append_sheet(workbook, consumptionSheet, 'Consumption Log');

  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

// Labels for consumption types
export const CONSUMPTION_TYPE_LABELS: Record<string, string> = {
  chipchip_sale: 'ChipChip Sale',
  b2b_delivery: 'B2B Delivery',
  damage: 'Damage',
  adjustment: 'Adjustment',
  other: 'Other',
};
